import { useState } from "react";

interface DragItem<T> {
  id: string;
  data?: T;
  type?: string;
}

interface UseDragAndDropOptions<T> {
  items: T[];
  setItems: (items: T[]) => void;
  getItemId: (item: T) => string;
  getItemType?: (item: T) => string;
  onReorder?: (items: T[]) => void;
  getGhostElement?: (item: T, dragElement: HTMLElement) => HTMLElement | null;
}

export const useDragAndDrop = <T,>({
  items,
  setItems,
  getItemId,
  getItemType,
  onReorder,
  getGhostElement,
}: UseDragAndDropOptions<T>) => {
  const [draggedItem, setDraggedItem] = useState<DragItem<T> | null>(null);
  const [dragOverItem, setDragOverItem] = useState<DragItem<T> | null>(null);

  const handleDragStart = (item: T, e: React.DragEvent<HTMLElement>) => {
    const itemId = getItemId(item);
    const itemType = getItemType?.(item) || "default";
    setDraggedItem({ id: itemId, data: item, type: itemType });
    setDragOverItem(null);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", itemId);
    e.dataTransfer.setData("application/item-type", itemType);

    // Create ghost image
    if (e.target instanceof HTMLElement) {
      // Use custom ghost element if provided, otherwise use the drag target
      const ghostSource = getGhostElement?.(item, e.target) || e.target;
      const ghostElem = ghostSource.cloneNode(true) as HTMLElement;
      ghostElem.style.position = "absolute";
      ghostElem.style.top = "-1000px";
      ghostElem.style.opacity = "0.5";
      ghostElem.style.width = `${ghostSource.offsetWidth}px`;
      ghostElem.style.backgroundColor = "#F6F6F6";
      ghostElem.style.pointerEvents = "none";
      document.body.appendChild(ghostElem);
      e.dataTransfer.setDragImage(ghostElem, 0, 0);
      setTimeout(() => document.body.removeChild(ghostElem), 0);
    }
  };

  const handleDragOver = (item: T, e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();

    // Check if the dragged item type matches the target item type
    const draggedType = draggedItem?.type || "default";
    const targetType = getItemType?.(item) || "default";

    // Only allow drop if types match
    if (draggedType !== targetType) {
      e.dataTransfer.dropEffect = "none";
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    e.dataTransfer.dropEffect = "move";
    const itemId = getItemId(item);
    setDragOverItem({ id: itemId, data: item, type: targetType });
  };

  const handleDrop = (targetItem: T, e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();

    if (!draggedItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Check if the dragged item type matches the target item type
    const draggedType = draggedItem?.type || "default";
    const targetType = getItemType?.(targetItem) || "default";

    // Only allow drop if types match
    if (draggedType !== targetType) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const draggedId = draggedItem.id;
    const targetId = getItemId(targetItem);

    if (draggedId === targetId) return;

    const draggedIndex = items.findIndex((item) => getItemId(item) === draggedId);
    const targetIndex = items.findIndex((item) => getItemId(item) === targetId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const newItems = [...items];
      const [draggedItemData] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, draggedItemData);

      setItems(newItems);
      onReorder?.(newItems);
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const isDragging = (item: T) => {
    return draggedItem?.id === getItemId(item);
  };

  const isDragOver = (item: T) => {
    return dragOverItem?.id === getItemId(item);
  };

  return {
    draggedItem,
    dragOverItem,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    isDragging,
    isDragOver,
  };
};
